import { PageSlugs } from "../add-element/types";
import { isUsingNewComponents } from "../add-element/utils";
import {
  Accommodation,
  Attendant,
  Comment,
  RawColorway,
  DekuraColorway,
  Guest,
  ImageUpload,
  Section,
  Page,
  Photo,
  Registry,
  Site,
  TimestampedModel,
  Video,
  WebsiteEvent,
  ThemeColors,
  ThemeColorCategory,
} from "../resources/modelTypes";
import {
  ReduxResource,
  ReduxResourceContainer,
  RootState,
} from "../resources/reduxTypes";
import { selectCollection } from "../resources/selectors";
import { selectStagedCreation } from "../resources/selectors";
import { getPageHasGeneralContent } from "../shared/utils";
import { ThemeAssetsSelector } from "./components/EditDesign/types";
import find from "lodash/find";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import keyBy from "lodash/keyBy";
import startsWith from "lodash/startsWith";
import transform from "lodash/transform";

export const getResource = <T extends TimestampedModel>(
  resource: ReduxResource<T>,
  includeStagedCreation: boolean = false,
): T[] => {
  const items = selectCollection(resource, {}).items.map(({ data }) => data);

  if (includeStagedCreation) {
    const stagedCreation = selectStagedCreation({}, resource);

    if (stagedCreation) {
      return items.concat(stagedCreation.data);
    }
  }

  return items;
};

export type AllResources = {
  accommodations: Accommodation[];
  attendants: Attendant[];
  comments: Comment[];
  guests: Guest[];
  photos: Photo[];
  publicEvents: WebsiteEvent[];
  registries: Registry[];
  rsvpEvents: WebsiteEvent[];
  videos: Video[];
};

export const getAllResources = (
  resources: ReduxResourceContainer,
  includeStagedCreation: boolean = false,
): AllResources => {
  const events = getResource(resources.events, includeStagedCreation) as [
    WebsiteEvent,
  ];

  return {
    accommodations: getResource(
      resources.accommodations,
      includeStagedCreation,
    ),
    attendants: getResource(resources.attendants, includeStagedCreation),
    comments: getResource(resources.comments),
    guests: getResource(resources.guests),
    photos: getResource(resources.photos),
    publicEvents: events.filter((event) => !event.private),
    registries: getResource(resources.registries, includeStagedCreation),
    rsvpEvents: events.filter((event) => event.rsvpEnabled),
    videos: getResource(resources.videos),
  };
};

export const getAllSections = (
  resources: ReduxResourceContainer,
  includeStagedCreation: boolean = false,
): Section[] => {
  const sectionsResource = resources.sections;
  const allSections: Section[] = [];

  sectionsResource.collections.forEach((collection) => {
    const collectionSections = selectCollection(
      sectionsResource,
      collection.params,
    );

    allSections.push(...collectionSections.items.map((item) => item.data));
  });

  if (includeStagedCreation) {
    sectionsResource.staged.create.forEach((stagedItem) => {
      if (stagedItem.data) {
        allSections.push(stagedItem.data);
      }
    });
  }

  return allSections;
};

export type PageData = {
  accommodations: Accommodation[];
  attendants: Attendant[];
  events: WebsiteEvent[];
  publicEvents: WebsiteEvent[];
  registries: Registry[];
  sites: Site[];
};

export const filterPage = (page: Page, data: PageData): boolean => {
  if (!page.isVisible) {
    return false;
  }
  const site = data.sites[0];

  if (isUsingNewComponents(site)) {
    // Always render the home page
    if (page.slug === PageSlugs.Home || page.slug === PageSlugs.RSVP) {
      return true;
    }
    const hasSections = !isEmpty(page.sections);

    // Everything except events uses only sections
    if (page.slug !== PageSlugs.Events) {
      return hasSections;
    }

    // Events checks for events OR sections
    const hasPublicEvents = !isEmpty(
      data.publicEvents === undefined
        ? data.events.filter((event) => !event.private)
        : data.publicEvents,
    );

    return hasPublicEvents || hasSections;
  }

  switch (page.slug) {
    case PageSlugs.Events:
      return (
        !isEmpty(
          data.publicEvents === undefined
            ? data.events.filter((event) => !event.private)
            : data.publicEvents,
        ) || getPageHasGeneralContent(page)
      );

    case PageSlugs.Accommodations:
      return !isEmpty(data.accommodations) || getPageHasGeneralContent(page);

    case PageSlugs.WeddingParty:
      return !isEmpty(data.attendants) || getPageHasGeneralContent(page);

    case PageSlugs.Photos:
      return !isEmpty(page.sections) || getPageHasGeneralContent(page);

    case PageSlugs.RSVP:
      return site.hasRsvpEvents || getPageHasGeneralContent(page);

    case PageSlugs.Registry:
      return !isEmpty(data.registries) || getPageHasGeneralContent(page);

    default:
      return true;
  }
};

export const selectSite = (sites: ReduxResource<Site>): Site => {
  const collection = selectCollection<Site>(sites, {});

  return collection.items[0].data;
};

export const selectAsset = (
  resources: ReduxResourceContainer,
  assetName: string,
  version: string,
): string | null => {
  const site = selectSite(resources.sites);
  const uuid = site?.assets ? site.assets[assetName] : null;

  // If asset value is a URL instead of a UUID, return it
  if (uuid && (startsWith(uuid, "http") || startsWith(uuid, "//"))) {
    return uuid;
  }

  const images = selectCollection<ImageUpload>(resources["image-uploads"], {});
  const result = find(images.items, (image) => image.data.uuid === uuid);

  return get(result, `data.versions.${version}`, null) || null;
};

export const selectAssets = (
  resources: ReduxResourceContainer,
): Record<string, string> => {
  const site = selectSite(resources.sites);
  const images = selectCollection(resources["image-uploads"], {}).items.map(
    (item) => item.data,
  );
  const keyedImages = keyBy(images, "uuid");

  return transform(
    site.assets,
    (result, value, key) => {
      result[key] = keyedImages[value];
    },
    {},
  );
};

export const selectDividerAssets = (state) => {
  const site: Site = selectSite(state.resources.sites);

  const mintedProductColorChoiceId = site.mintedProductColorChoiceId;
  const colorwayCode = state.theme.colorwayCodeById[mintedProductColorChoiceId];
  const colorways = state.theme.dekuraData?.colorways || [];
  const selected = colorways.find((cw) => cw.colorway === colorwayCode);

  return {
    bgColor: selected?.required?.colors?.backgroundColor,
    graphUrl: selected?.required?.artwork?.SmallDivider,
  };
};

export const selectThemeAssets = (state: RootState): ThemeAssetsSelector => {
  const site: Site = selectSite(state.resources.sites);
  // All colorways for the theme are stored in redux
  const themeColorways: RawColorway[] = state.theme.colorways;
  const mintedProductColorChoiceId: string = site.mintedProductColorChoiceId;
  const selectedColorway: RawColorway | undefined = themeColorways.find(
    (colorway: RawColorway) =>
      colorway.external_id === mintedProductColorChoiceId,
  );

  // Getting colorway code from the selected product color choice ID
  const colorwayCode: Record<number, string> =
    state.theme.colorwayCodeById[mintedProductColorChoiceId];
  const colorways: DekuraColorway[] = state.theme.dekuraData?.colorways || [];
  const selectedColorwayGroup: DekuraColorway | undefined = colorways.find(
    (cw: DekuraColorway) => cw.colorway === colorwayCode,
  );

  // Flatten colors for component consumption
  const currentThemeColors: ThemeColors = {
    [ThemeColorCategory.background]:
      selectedColorwayGroup?.required?.colors?.backgroundColor || "#ffffff",
    [ThemeColorCategory.text]:
      selectedColorwayGroup?.required?.colors?.fontColor || "#000000",
    [ThemeColorCategory.secondaryBackground]:
      selectedColorwayGroup?.header?.TopNavbar?.backgroundColor ||
      selectedColorwayGroup?.header?.InlineNavbar?.backgroundColor ||
      selectedColorwayGroup?.header?.IntroContainer?.backgroundColor ||
      "#ffffff",
    [ThemeColorCategory.secondaryText]:
      selectedColorwayGroup?.header?.TopNavbar?.linkColor ||
      selectedColorwayGroup?.header?.InlineNavbar?.linkColor ||
      selectedColorwayGroup?.header?.IntroContainer?.fontColor ||
      "#000000",
    [ThemeColorCategory.linkAndButtonText]:
      selectedColorwayGroup?.required?.colors?.linkColor ||
      selectedColorwayGroup?.required?.colors?.Button ||
      "#000000",
  };

  return {
    colorways: themeColorways,
    selectedColorway,
    site,
    themeColors: currentThemeColors,
  };
};
